import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Alert,
  Box,
  Divider,
  Autocomplete,
  AppBar,
  Toolbar,
  IconButton,
  useTheme,
  useMediaQuery
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import { LocalizationProvider, DateTimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';

const LogFlightDialog = ({
  open,
  onClose,
  onSubmit,
  drone,
  drones = [], // List of drones for selection (if drone not pre-selected)
  pilots = [], // List of pilots in organization
  operation = null, // Optional: pre-fill from mission log
  flightLog = null, // Optional: specific flight log to edit (null = create new)
  currentUserId,
  pilotName = null, // Optional: pre-determined pilot name (from operation)
  pilotId = null // Optional: pre-determined pilot ID (from operation)
}) => {
  const [formData, setFormData] = useState({
    droneId: '',
    pilotId: '',
    flightDate: dayjs(),
    flightDuration: '',
    location: '',
    purpose: '',
    notes: '',
    weatherConditions: '',
    operationId: null
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [validationMessage, setValidationMessage] = useState(null);
  const successTimerRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // isEditMode is driven by the explicit flightLog prop, not operation.flightLog
  const isEditMode = !!flightLog;

  // Initialize form data
  useEffect(() => {
    if (open) {
      if (isEditMode) {
        // Edit mode — pre-fill from the specific flight log being edited
        setFormData({
          droneId: flightLog.drone?.id || drone?.id || '',
          pilotId: flightLog.pilot?.id || pilotId || currentUserId || '',
          flightDate: flightLog.flightDate ? dayjs(flightLog.flightDate) : dayjs(),
          flightDuration: flightLog.flightDuration ?? '',
          location: flightLog.location || '',
          purpose: flightLog.purpose || '',
          notes: flightLog.notes || '',
          weatherConditions: flightLog.weatherConditions || '',
          operationId: operation?.id || null
        });
      } else {
        // Create mode — pre-fill from operation metadata
        setFormData({
          droneId: drone?.id || '',
          pilotId: pilotId || currentUserId || '',
          flightDate: operation?.selected_date_time ? dayjs(operation.selected_date_time) : dayjs(),
          flightDuration: operation?.duration ? (operation.duration / 60).toFixed(2) : '',
          location: operation?.operation_metadata?.layers?.operationLocation || '',
          purpose: operation ? 'LAANC Authorization Flight' : '',
          notes: '',
          weatherConditions: '',
          operationId: operation?.id || null
        });
      }
      setErrors({});
      setSubmitError(null);
      setSubmitSuccess(false);
      setValidationMessage(null);
      // Clear any pending auto-close timer from a previous edit
      if (successTimerRef.current) {
        clearTimeout(successTimerRef.current);
        successTimerRef.current = null;
      }
    }
  }, [open, drone, operation, flightLog, currentUserId, pilotId]);

  // Clean up timer if component unmounts while banner is showing
  useEffect(() => {
    return () => {
      if (successTimerRef.current) clearTimeout(successTimerRef.current);
    };
  }, []);

  const handleChange = (field) => (event) => {
    const value = event.target ? event.target.value : event;
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));
    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: null
      }));
    }
    // Clear validation message when user starts fixing fields
    if (validationMessage) {
      setValidationMessage(null);
    }
  };

  const handleDateChange = (newDate) => {
    setFormData((prev) => ({
      ...prev,
      flightDate: newDate
    }));
    if (errors.flightDate) {
      setErrors((prev) => ({ ...prev, flightDate: null }));
    }
    if (validationMessage) {
      setValidationMessage(null);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.droneId) {
      newErrors.droneId = 'Drone is required';
    }
    if (!formData.pilotId) {
      newErrors.pilotId = 'Pilot is required';
    }
    if (!formData.flightDate) {
      newErrors.flightDate = 'Flight date/time is required';
    }
    if (!formData.flightDuration || parseFloat(formData.flightDuration) <= 0) {
      newErrors.flightDuration = 'Flight duration must be greater than 0';
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      const missingFields = Object.values(newErrors).join(', ');
      setValidationMessage(missingFields);
      return false;
    }

    setValidationMessage(null);
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    setSubmitError(null);

    try {
      // Convert dayjs object to ISO string for API
      const submitData = {
        ...formData,
        flightDate: formData.flightDate.toISOString()
      };
      await onSubmit(submitData);

      if (isEditMode) {
        // Show inline success banner, then auto-close after 1.5 s
        setSubmitSuccess(true);
        successTimerRef.current = setTimeout(() => {
          successTimerRef.current = null;
          onClose();
        }, 1500);
      } else {
        onClose();
      }
    } catch (err) {
      setSubmitError(err.response?.data?.error || err.message || 'Failed to log flight');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting && !submitSuccess) {
      onClose();
    }
  };

  const selectedDrone = drone || drones.find(d => d.id === formData.droneId);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth fullScreen={isMobile}>
      {isMobile && (
        <AppBar sx={{ position: 'relative' }}>
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={handleClose}
              disabled={submitting || submitSuccess}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
            <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
              {isEditMode ? 'Edit Flight Log' : 'Log Flight Hours'}
            </Typography>
            <Button
              autoFocus
              color="inherit"
              onClick={handleSubmit}
              disabled={submitting || submitSuccess}
            >
              {submitting ? (isEditMode ? 'Saving...' : 'Logging...') : (isEditMode ? 'Save' : 'Log')}
            </Button>
          </Toolbar>
        </AppBar>
      )}
      {!isMobile && (
        <DialogTitle>
          <Typography variant="h5">{isEditMode ? 'Edit Flight Log' : 'Log Flight Hours'}</Typography>
          <Typography variant="body2" color="text.secondary">
            {isEditMode
              ? 'Update the flight log record for this operation'
              : `Record a flight for ${selectedDrone ? `${selectedDrone.manufacturer} ${selectedDrone.model}` : 'a drone'}`}
          </Typography>
        </DialogTitle>
      )}
      <DialogContent dividers={!isMobile} sx={{ p: isMobile ? 2 : 3 }}>
        {submitSuccess && (
          <Alert
            severity="success"
            icon={<CheckCircleIcon fontSize="inherit" />}
            sx={{ mb: 2, fontWeight: 600 }}
          >
            Flight log updated successfully. Closing…
          </Alert>
        )}

        {submitError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {submitError}
          </Alert>
        )}

        {operation && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Logging flight from Mission Log operation
          </Alert>
        )}

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Grid container spacing={isMobile ? 2 : 3}>
            {/* Drone Selection (if not pre-selected) */}
            {!drone && drones.length > 0 && (
              <Grid item xs={12}>
                <FormControl fullWidth required error={!!errors.droneId}>
                  <InputLabel>Drone</InputLabel>
                  <Select
                    value={formData.droneId}
                    onChange={handleChange('droneId')}
                    label="Drone"
                  >
                    {drones.map((d) => (
                      <MenuItem key={d.id} value={d.id}>
                        {d.manufacturer} {d.model} ({d.serialNumber})
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.droneId && (
                    <Typography variant="caption" color="error">
                      {errors.droneId}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
            )}

            {/* Pilot Selection */}
            <Grid item xs={12} sm={6}>
              {pilotName ? (
                // Read-only pilot display when coming from operation
                <TextField
                  fullWidth
                  required
                  label="Pilot"
                  value={pilotName}
                  disabled
                  helperText="Pilot from operation"
                />
              ) : (
                // Dropdown for manual pilot selection
                <FormControl fullWidth required error={!!errors.pilotId}>
                  <InputLabel>Pilot</InputLabel>
                  <Select
                    value={formData.pilotId}
                    onChange={handleChange('pilotId')}
                    label="Pilot"
                  >
                    {pilots.map((pilot) => (
                      <MenuItem key={pilot.id} value={pilot.id}>
                        {pilot.firstName} {pilot.lastName}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.pilotId && (
                    <Typography variant="caption" color="error">
                      {errors.pilotId}
                    </Typography>
                  )}
                </FormControl>
              )}
            </Grid>

            {/* Flight Date/Time */}
            <Grid item xs={12} sm={6}>
              <DateTimePicker
                label="Flight Date & Time *"
                value={formData.flightDate}
                onChange={handleDateChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    required
                    error={!!errors.flightDate}
                    helperText={errors.flightDate}
                  />
                )}
                maxDateTime={dayjs()} // Can't log future flights
              />
            </Grid>

            {/* Flight Duration */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                type="number"
                label="Flight Duration (hours)"
                value={formData.flightDuration}
                onChange={handleChange('flightDuration')}
                error={!!errors.flightDuration}
                helperText={errors.flightDuration || 'e.g., 1.5 for 1 hour 30 minutes'}
                inputProps={{ min: 0, step: 0.1 }}
              />
            </Grid>

            {/* Location */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Location"
                value={formData.location}
                onChange={handleChange('location')}
                helperText="Flight location or area"
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
            </Grid>

            {/* Purpose */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Flight Purpose"
                value={formData.purpose}
                onChange={handleChange('purpose')}
                helperText="e.g., Survey, Inspection, Training, Photography"
              />
            </Grid>

            {/* Weather Conditions */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Weather Conditions"
                value={formData.weatherConditions}
                onChange={handleChange('weatherConditions')}
                helperText="e.g., Clear, Partly Cloudy, Windy"
              />
            </Grid>

            {/* Notes */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Notes"
                value={formData.notes}
                onChange={handleChange('notes')}
                helperText="Additional flight details or observations"
              />
            </Grid>
          </Grid>
        </LocalizationProvider>
      </DialogContent>

      {!isMobile && (
        <DialogActions sx={{ px: 3, pb: 2, flexWrap: 'wrap', gap: 1 }}>
          {validationMessage && (
            <Typography variant="caption" color="error" sx={{ flex: 1, alignSelf: 'center' }}>
              {validationMessage}
            </Typography>
          )}
          <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
            <Button onClick={handleClose} disabled={submitting || submitSuccess}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={submitting || submitSuccess}
              size="large"
            >
              {submitting ? (isEditMode ? 'Saving...' : 'Logging...') : (isEditMode ? 'Save Changes' : 'Log Flight')}
            </Button>
          </Box>
        </DialogActions>
      )}
      {isMobile && validationMessage && (
        <Box sx={{ px: 2, pb: 2 }}>
          <Alert severity="error" sx={{ fontSize: '0.85rem' }}>
            {validationMessage}
          </Alert>
        </Box>
      )}
    </Dialog>
  );
};

export default LogFlightDialog;
